"use client";
import { useSectionNavTheme } from "@/app/hooks/useSectionNavTheme";
import { useGSAP } from "@gsap/react";
import gsap from "gsap";
import { useRef } from "react";
import { Heading2 } from "../typography";
import { ScrollItem } from "../nsw-water-compliance/scroll-item";
import { TheResults } from "./the-results";
import { LazyDotLottie } from "../shared/lazy-dotlottie";
import Image from "next/image";

export const HowItWorks = () => {
  const sectionRef = useRef<HTMLDivElement | null>(null);
  const contentRef = useRef<HTMLDivElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const scrollItemsRef = useRef<(HTMLDivElement | null)[]>([]);

  // Calculate scroll distance - reusable function
  const calculateScrollDistance = () => {
    const content = contentRef.current;
    const scrollItems = scrollItemsRef.current.filter(Boolean);

    if (!content || !scrollItems.length) return 0;

    const gap = parseFloat(getComputedStyle(content).gap) || 0;
    const totalWidth =
      scrollItems.length * (scrollItems[0]?.offsetWidth ?? 0) +
      gap * (scrollItems.length - 1);
    const containerWidth = containerRef.current?.offsetWidth || 0;

    return totalWidth - containerWidth;
  };

  useSectionNavTheme({
    trigger: "#act-how-it-works",
    isDark: false,
    // markers: true,
    end: () => {
      const scrollDistance = calculateScrollDistance();
      return `+=${scrollDistance + window.innerHeight + 160}`; // Added extra offset for better timing
    },
  });

  useGSAP(() => {
    const mm = gsap.matchMedia();

    mm.add("(min-width: 1024px)", () => {
      const section = sectionRef.current;
      const content = contentRef.current;
      const container = containerRef.current;
      const scrollItems = scrollItemsRef.current.filter(Boolean);

      if (!section || !content || !scrollItems.length) return;

      // Initial title animation
      const titleTl = gsap.timeline({
        scrollTrigger: {
          trigger: section,
          start: "top 80%",
          toggleActions: "play none none reverse",
        },
      });

      titleTl.from(".works-title", {
        opacity: 0,
        y: 40,
        duration: 0.8,
        ease: "power2.out",
      });

      // Get scroll distance using the shared calculation function
      const scrollDistance = calculateScrollDistance();

      // Create horizontal scroll timeline
      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: section,
          start: "top top",
          end: () => `+=${scrollDistance}`,
          scrub: 1,
          pin: container,
          anticipatePin: 1,
          invalidateOnRefresh: true,
          // markers: true,
          onUpdate: (self) => {
            // Animate progress bars based on scroll progress
            const progress = self.progress;

            // Update progress bars for each scroll item
            scrollItems.forEach((item, index) => {
              const progressBar = item?.querySelector(".progress-bar");
              if (progressBar) {
                const itemProgress = Math.max(
                  0,
                  Math.min(1, progress * scrollItems.length - index)
                );
                gsap.set(progressBar, {
                  clipPath: `polygon(0% 0%, ${itemProgress * 100}% 0%, ${
                    itemProgress * 100
                  }% 100%, 0% 100%)`,
                });
              }
            });
          },
        },
      });

      // Animate horizontal movement
      tl.to(scrollItems, {
        x: -scrollDistance,
        duration: 1,
        ease: "none",
      });

      // Add stagger animation for scroll items appearing
      gsap.fromTo(
        scrollItems,
        {
          opacity: 0,
          y: 50,
        },
        {
          opacity: 1,
          y: 0,
          duration: 1,
          stagger: 0.2,
          scrollTrigger: {
            trigger: section,
            start: "top 80%",
            end: "top 20%",
            scrub: false,
          },
        }
      );
    });
  });

  const workflowSteps = [
    {
      title: "Proprietary AI Processing",
      description:
        "Haizea's specialized AI methods analysed high-resolution airborne imagery and LiDAR data, providing detailed mapping of urban growth and vegetation changes over 20 years.",
      icon: (
        <LazyDotLottie
          src="/lottie/ai-chip.lottie"
          fallbackSrc="/icons/ai.webp"
          className="mx-auto w-[150px] lg:w-[200px] -my-4 lg:-my-6 aspect-square"
          alt="Data Dashboard Animation"
          speed={0.9}
        />
      ),
    },
    {
      title: "Multi-temporal Analysis",
      description:
        "Processed 20 years of satellite data (2004-2024) to track building footprint expansion, vegetation loss, and ecosystem impacts with unprecedented spatial and temporal detail.",
      icon: (
        <LazyDotLottie
          src="/lottie/air-clock.lottie"
          fallbackSrc="/icons/multi-temporal.webp"
          className="mx-auto w-[150px] lg:w-[200px] -my-4 lg:-my-6 aspect-square"
          alt="Data Dashboard Animation"
        />
      ),
    },
    {
      title: "Species Impact Assessment",
      description:
        "Analysed relationships between urban development and populations of 29 threatened species affected by habitat loss, providing objective evidence for conservation priorities.",
      icon: (
        <LazyDotLottie
          src="/lottie/impact-assessment.lottie"
          fallbackSrc="/icons/impact-assessment.webp"
          className="mx-auto w-[150px] lg:w-[200px] -my-4 lg:-my-6 aspect-square"
          alt="Data Dashboard Animation"
        />
      ),
    },
    {
      title: "Interactive Data Explorer",
      description:
        "Created a comprehensive web-based tool at act.terrak.io enabling users to explore land use changes, vegetation trends, and biodiversity impacts with real-time analytics.",
      icon: (
        <LazyDotLottie
          src="/lottie/data-dashboard.lottie"
          fallbackSrc="/icons/data-dashboard.webp"
          className="mx-auto w-[150px] lg:w-[240px] -my-4 lg:-my-10 aspect-square"
          alt="Data Dashboard Animation"
          speed={0.8}
        />
      ),
    },
    {
      title: "Evidence-Based Policy",
      description:
        "Replaced subjective assessments with verifiable data, enabling predictive capabilities and proactive environmental management backed by objective measurements.",
      icon: (
        <LazyDotLottie
          src="/lottie/evidence-based-policy.lottie"
          fallbackSrc="/icons/evidence-based-policy.webp"
          className="mx-auto w-[150px] lg:w-[250px] -my-4 lg:-my-12 aspect-square"
          alt="Evidence Based Policy Animation"
        />
      ),
    },
  ];

  return (
    <section
      ref={sectionRef}
      id="act-how-it-works"
      className="relative py-20 lg:py-24 bg-haizea-dark text-white overflow-hidden"
    >
      {/* Background Pattern - Abstract Blobs */}
      <div className="absolute inset-0 opacity-20 pointer-events-none">
        {/* Orange Blob - Top Left */}
        <div className="absolute -top-20 -left-20 w-96 h-96 bg-haizea-orange/60 rounded-full blur-3xl animate-pulse" />

        {/* Blue Blob - Top Right */}
        <div
          className="absolute top-40 -right-32 w-[500px] h-[500px] bg-haizea-blue/60 rounded-full blur-3xl"
          style={{ animationDelay: "2s" }}
        />

        {/* Green Blob - Middle Left */}
        <div
          className="absolute top-1/2 -left-40 w-[600px] h-[600px] bg-haizea-green/60 rounded-full blur-3xl animate-pulse"
          style={{ animationDelay: "4s" }}
        />

        {/* Orange Blob - Bottom Right */}
        <div className="absolute -bottom-40 right-20 w-[550px] h-[550px] bg-haizea-orange/60 rounded-full blur-3xl" />

        {/* Small accent blobs */}
        <div
          className="absolute top-1/4 right-1/4 w-64 h-64 bg-haizea-blue/50 rounded-full blur-2xl animate-pulse"
          style={{ animationDelay: "1s" }}
        />
        <div
          className="absolute bottom-1/4 left-1/3 w-80 h-80 bg-haizea-green/60 rounded-full blur-3xl"
          style={{ animationDelay: "3s" }}
        />
      </div>
      <Image
        src="/images/satellite-image.webp"
        alt="Background Satellite Image"
        height={350}
        width={430}
        className="absolute bottom-[50vw] -left-[30%] lg:-left-[25%] xl:-left-[5%] xl:w-120 opacity-90 z-10 hidden lg:block drop-shadow-2xl/80"
        data-speed="0.6"
        draggable={false}
      />
      <Image
        src="/images/bird.webp"
        alt="Background Bird"
        height={250}
        width={330}
        className="absolute bottom-96 -right-[10%] xl:right-0 opacity-90 z-10 hidden lg:block w-96 h-auto drop-shadow-2xl/50"
        draggable={false}
        data-speed="0.3"
      />

      {/* Horizontal Scroll Section */}
      <div ref={containerRef} className="container">
        <div className="text-center mb-16">
          <Heading2 className="lg:mb-4 works-title">How It Works</Heading2>
          <p className="text-xl md:text-2xl text-slate-300 max-w-3xl mx-auto">
            From subjective assessments to evidence-based environmental policy
            through 20 years of satellite analysis
          </p>
        </div>
        <div
          ref={contentRef}
          className="flex flex-wrap lg:flex-nowrap gap-8 overflow-visible"
        >
          {workflowSteps.map((step, index) => (
            <ScrollItem
              key={index}
              ref={(el) => {
                scrollItemsRef.current[index] = el;
              }}
              title={step.title}
              description={step.description}
            >
              {step.icon}
            </ScrollItem>
          ))}
        </div>
      </div>
      <TheResults />
    </section>
  );
};
